import React, { useCallback, useEffect, useRef, useState } from "react";

import Alert from "veneer/Alert";
import Drawer from "veneer/Drawer";
import { showError, showSuccess } from "veneer/SnackBar";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";

import {
  RESOLVE_INCIDENT,
  RESOLVE_INCIDENT_SUCCESS,
} from "cortex/ui/src/fleet/redux-store/ui-state/incidents/actions";
import { incidentsService } from "cortex/ui/src/fleet/service/incidents";
import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import { Incident } from "cortex/ui/src/zeus/view/incidents/model";

interface ResolveIncidentDrawerProps {
  onCancel: () => void;
  open: boolean;
  selectedItems: ReadonlyArray<Incident>;
  setSelectedItems: (items: ReadonlyArray<Incident>) => void;
}

export default function ResolveIncidentDrawer({
  onCancel,
  open,
  selectedItems,
  setSelectedItems,
}: ResolveIncidentDrawerProps) {
  const dispatch = useAppDispatch();
  const [resolutionComment, setResolutionComment] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const isMountedRef = useRef(true);

  const trimmedComment = resolutionComment.trim();

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (open) {
      setResolutionComment("");
    }
  }, [open, selectedItems]);

  const handleCancel = useCallback(() => {
    if (submitting) {
      return;
    }

    if (isMountedRef.current) {
      setResolutionComment("");
    }

    onCancel();
  }, [onCancel, submitting]);

  const resolveIncident = useCallback(
    (id: Incident["id"], comment: string) =>
      createPromiseApiAction(dispatch, RESOLVE_INCIDENT, [id, { comment }]),
    [dispatch],
  );

  const resolveIncidentsBulk = useCallback(
    (incidentIds: ReadonlyArray<Incident["id"]>, comment: string) =>
      incidentsService.resolveBulk({
        comment,
        incident_ids: incidentIds,
      }),
    [],
  );

  const handleResolveIncident = useCallback(async () => {
    if (!trimmedComment || submitting) {
      return;
    }

    setSubmitting(true);

    try {
      if (selectedItems.length === 1) {
        await resolveIncident(selectedItems[0].id, trimmedComment);

        showSuccess("Incident resolved.");
        setSelectedItems([]);
        setResolutionComment("");
        onCancel();
        return;
      }

      const response = await resolveIncidentsBulk(
        selectedItems.map(({ id }) => id),
        trimmedComment,
      );
      const resolvedIncidents = response?.data?.data ?? [];

      resolvedIncidents.forEach((incident: Incident) => {
        dispatch({
          payload: {
            data: incident,
          },
          type: RESOLVE_INCIDENT_SUCCESS,
        });
      });

      showSuccess(`${selectedItems.length} incidents resolved.`);
      setSelectedItems([]);
      setResolutionComment("");
      onCancel();
    } catch {
      showError(
        selectedItems.length === 1
          ? "Failed to resolve incident."
          : "Failed to resolve incidents.",
      );
      return;
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [
    dispatch,
    onCancel,
    resolveIncidentsBulk,
    selectedItems,
    resolveIncident,
    setSelectedItems,
    submitting,
    trimmedComment,
  ]);

  return (
    <Drawer
      commitDisabled={submitting || !resolutionComment.trim()}
      commitLabel="Resolve"
      commitLoading={submitting}
      onCancel={handleCancel}
      onClose={handleCancel}
      onCommit={handleResolveIncident}
      open={open}
      title="Resolution Details"
    >
      <Stack spacing={2}>
        {selectedItems.length > 1 && (
          <Alert
            dense
            severity="info"
            title={`You are about to resolve ${selectedItems.length} incident(s).`}
          >
            Enter the resolution details in the text box below and click
            "Resolve" to complete this action. The same comment will be applied
            to all selected incidents.
          </Alert>
        )}
        <TextInput
          autoFocus
          fullWidth
          helperText="Only enter resolution details as needed. We will automatically record the resolution date and user who made this change."
          label="Enter resolution comment"
          onChange={(newValue) => setResolutionComment(newValue ?? "")}
          value={resolutionComment}
        />
      </Stack>
    </Drawer>
  );
}
